import type { FP_FulfillmentContextService } from '.';
import { addresses, contacts, fulfillmentContexts, tenants, fulfillmentContextDetail } from './fixtures';
import delay from '../mockServiceDelay';
import type {
  CarrierSettingsCreateParams,
  CarrierSettingsUpdateParams,
  ContractUpdateParams,
  CreateFulfillmentContextParams,
  CreateOriginParams,
  CreateTenantParams,
  LinkAddressParams,
  LinkAddressToReturnToAddressOverride,
  LinkContactParams,
  LinkContactToReturnToAddressOverride,
  LinkLabelPropertyParams,
  LinkOriginParams,
  LinkReturnAddressOverrideParams,
  LinkScheduleParams,
  LinkTagParams,
  LinkTenantParams,
  UnlinkTenant,
  UpdateFulfillmentContextParams,
  UpdateOriginParams,
  UpdateReturnToAddressOverrideParams,
  UpdateTenantParams,
} from '../../fulfillmentContext/types';
import type {
  AddressCreateParams,
  AddressUpdateParams,
  ContactCreateParams,
  ContactUpdateParams,
  LabelPropertiesCreateParams,
  LabelPropertiesUpdateParams,
  ScheduleCreateParams,
  ScheduleUpdateParams,
} from './types';
import type { PartnerReturnToAddressOverrideCreate } from '@shipium-corp/shipium-ts-types';

export const mockFulfillmentContextService: FP_FulfillmentContextService = {
  fetchFulfillmentContexts: async () => {
    await delay();
    return [...fulfillmentContexts];
  },
  fetchTenants: async () => {
    return [...tenants];
  },
  fetchFulfillmentContextDetail: async (fulfillmentContextId: string, tenantId?: string) => {
    await delay();

    return fulfillmentContextDetail;
  },
  createFulfillmentContext: async (payload: CreateFulfillmentContextParams) => {
    await delay();
    return '123456';
  },
  deleteFulfillmentContext: async (contextId: string) => {
    await delay();
  },
  updateFulfillmentContext: async (contextParams: UpdateFulfillmentContextParams) => {
    await delay();
  },
  updateTenant: async (tenantParams: UpdateTenantParams) => {
    await delay();
  },
  updateReturnAddressOverride: async (tenantParams: UpdateReturnToAddressOverrideParams) => {
    await delay();
    return {
      partnerId: 'string',
      returnToAddressOverrideId: 'string',
      partnerProvidedId: 'string',
      name: 'string',
      description: 'string',
      carrierIds: [],
      tenantIds: [],
      active: true,
      archived: false,
      auditInfo: {
        createdBy: '',
        lastUpdatedBy: '',
        lastUpdatedTimeStamp: '',
        creationTimeStamp: '',
        version: 1,
      },
    };
  },
  unlinkReturnToAddressOverrideFromFulfillmentContext: async (params: LinkReturnAddressOverrideParams) => {
    await delay();
  },
  linkReturnToAddressOverrideFromFulfillmentContext: async (params: LinkReturnAddressOverrideParams) => {
    await delay();
  },
  linkTenant: async (tenantParams: LinkTenantParams) => {
    await delay();
  },
  unlinkTenant: async (tenantParams: UnlinkTenant) => {
    await delay();
  },
  createTenant: async (tenantParams: CreateTenantParams) => {
    await delay();
    return '1234';
  },
  createReturnToAddressOverride: async (
    returnToAddressOverrideParams: PartnerReturnToAddressOverrideCreate & { fulfillmentContextId: string },
  ) => {
    await delay();
    return '1234';
  },
  linkAddressToReturnAddressOverride: async (params: LinkAddressToReturnToAddressOverride) => {
    await delay();
  },
  fetchInjectionProfileById: async (params: 'identifier') => {
    await delay();
  },
  unlinkAddressFromReturnAddressOverride: async (params: LinkAddressToReturnToAddressOverride) => {
    await delay();
  },
  linkContactToReturnAddressOverride: async (params: LinkContactToReturnToAddressOverride) => {
    await delay();
  },
  unlinkContactFromReturnAddressOverride: async (params: LinkContactToReturnToAddressOverride) => {
    await delay();
  },
  linkReturnAddressOverride: async (returnToAddressOverrideParams: LinkReturnAddressOverrideParams) => {
    await delay();
  },
  fetchAddresses: async () => {
    await delay();
    return [...addresses];
  },
  fetchAddressBook: async () => {
    await delay();
    return [];
  },
  createAddress: async (params: AddressCreateParams) => {
    await delay();
    return { ...params, addressId: '1234' } as any;
  },
  updateAddress: async (params: AddressUpdateParams) => {
    await delay();
    return { ...params, addressId: '1234' } as any;
  },
  deleteAddress: async (addressId: string) => {
    await delay();
    return { addressId, archived: true, active: false } as any;
  },
  linkAddress: async (params: LinkAddressParams) => {
    await delay();
    return { ...params, addressId: '1234' } as any;
  },
  fetchLabelProperties: async () => {
    await delay();
    return [];
  },
  createLabelProperties: async (params: LabelPropertiesCreateParams) => {
    await delay();
    return { ...params, labelPropertiesId: '1234' } as any;
  },
  updateLabelProperties: async (params: LabelPropertiesUpdateParams) => {
    await delay();
    return { ...params, labelPropertiesId: '1234' } as any;
  },
  linkLabelProperty: async (params: LinkLabelPropertyParams) => {
    await delay();
  },
  fetchContacts: async () => {
    await delay();
    return [...contacts];
  },
  fetchSelectionModifiers: async () => {
    await delay();
    return [];
  },
  fetchCarrierAccounts: async () => {
    await delay();
    return [];
  },
  fetchContracts: async () => {
    await delay();
    return [];
  },
  fetchCarrierSettings: async () => {
    await delay();
    return [];
  },
  fetchCarrierRateSets: async () => {
    await delay();
    return [];
  },
  createContact: async (params: ContactCreateParams) => {
    await delay();
    return { ...params, addressId: '1234' } as any;
  },
  updateContact: async (params: ContactUpdateParams) => {
    await delay();
    return { ...params, addressId: '1234' } as any;
  },
  linkContact: async (params: LinkContactParams) => {
    await delay();
  },
  linkTag: async (params: LinkTagParams) => {
    await delay();
  },
  fetchOrigins: async () => {
    await delay();
    return [];
  },
  fetchThirdPartyBillingAccounts: async () => {
    await delay();
    return [];
  },
  fetchThirdPartyBillingAccountSets: async () => {
    await delay();
    return [];
  },
  fetchSchedules: async () => {
    await delay();
    return [];
  },
  fetchTags: async () => {
    await delay();
    return [];
  },

  createSchedule: async (params: ScheduleCreateParams) => {
    await delay();
    return { ...params, scheduleId: '1234' } as any;
  },
  updateSchedule: async (params: ScheduleUpdateParams) => {
    await delay();
    return { ...params } as any;
  },
  linkSchedule: async (params: LinkScheduleParams) => {
    await delay();
  },
  fetchPackaging: async () => {
    await delay();
    return [];
  },
  fetchPackagingSets: async () => {
    await delay();
    return [];
  },
  linkOrigin: async (params: LinkOriginParams) => {
    await delay();
  },
  createOrigin: async (params: CreateOriginParams) => {
    await delay();
    return '1234';
  },
  updateOrigin: async (params: CreateOriginParams) => {
    await delay();
    return '1234';
  },
  createOriginWithAssets: async (params: CreateOriginParams) => {
    await delay();
    return '123456';
  },
  updateOriginWithAssets: async (params: UpdateOriginParams) => {
    await delay();
  },
  fetchLabelAugmentations: async () => {
    await delay();
    return [];
  },
  fetchAugmentationSets: async () => {
    await delay();
    return [];
  },
  linkLabelAugmentation: async () => {
    await delay();
  },
  createLabelAugmentation: async () => {
    await delay();
    return '1234';
  },
  updateLabelAugmentation: async () => {
    await delay();
    return {} as any;
  },
  updateCarrierSettings: async (params: CarrierSettingsUpdateParams) => {
    await delay();
  },
  createCarrierSettings: async (params: CarrierSettingsCreateParams) => {
    await delay();
  },
  linkCarrierSettings: async () => {
    await delay();
  },
  linkCarrierContract: async () => {
    await delay();
  },
  createContract: async () => {
    return [];
  },
  updateContract: async (params: ContractUpdateParams) => {
    await delay();
    return {} as any;
  },
  linkCarrierAccount: async () => {
    await delay();
  },
  createCarrierAccount: async () => {
    await delay();
  },
  updateCarrierAccount: async () => {
    await delay();
  },
  fetchReturnSettings: async () => {
    await delay();
    return [];
  },
  linkReturnAddressToOrigin: async () => {
    await delay();
    return;
  },
  linkReturnContactToOrigin: async () => {
    await delay();
    return;
  },
  fetchInjectionProfiles: async () => {},
  createInjectionProfile: async () => {},
  updateInjectionProfile: async () => {},
  linkToInjectionProfile: async (payload) => {},
  fetchInjectionSites: async () => {},
  createInjectionSite: async (payload) => {},
  fetchDockLane: async (payload) => {},
  fetchDockLanes: async (payload) => {},
  fetchDockLaneSortConfiguration: async (payload) => {},
  fetchDockLaneSchedule: async (payload) => {},
  fetchDockLanePostalMapping: async (payload) => {},
  updateInjectionSite: async (payload) => {},
  linkToInjectionSite: async (payload) => {},
  createCarrierRateSet: async (payload) => {},
  fetchCarrierRateSet: async (payload) => ({}) as any,
  createThirdPartyBillingAccount: async (payload) => {
    await delay();
  },
  linkThirdPartyBillingAccount: async (payload) => {
    await delay();
  },
  createThirdPartyBillingAccountSet: async (payload) => {
    await delay();
    return {} as any;
  },
  updateThirdPartyBillingAccountSet: async (payload) => {
    await delay();
    return {} as any;
  },
  getThirdPartyBillingAccountSet: async (payload) => {
    await delay();
    return {} as any;
  },
  linkThirdPartyBillingAccountSet: async (payload) => {
    await delay();
  },
  bulkLinkOriginToContexts: async (payload) => {
    await delay();
    return { failedContexts: [], linkedContexts: [] };
  },
  fetchUpsRegistrations: async (payload) => ({}) as any,
  generateUPSRegistration: async (payload) => ({}) as any,
  disconnectUPSRegistration: async (payload) => ({}) as any,
  fetchFedexRegistrations: async (payload) => ({}) as any,
  fetchFedexRegistration: async (payload) => ({}) as any,
  fetchCarrierAccount: async (payload) => ({}) as any,
  fetchAddress: async (payload) => ({}) as any,
  validateFedexAccountAddress: async (payload) => ({}) as any,
  validateFedexAccountInvoice: async (payload) => ({}) as any,
  generateFedexAccountPin: async (payload) => ({}) as any,
  validateFedexAccountPin: async (payload) => ({}) as any,
  resetFedexRegistration: async (payload) => ({}) as any,
  disconnectAmazonRegistration: async (payload) => ({}) as any,
};
export default mockFulfillmentContextService;
